import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams, Link } from "react-router-dom";
import {
  getClubSessions,
  getTeamAthletes,
  setAttendance,
  getSessionAttachments,
  uploadSessionAttachment,
  deleteSessionAttachment,
  type ClubSession,
  type Athlete,
  type SessionAttachment,
} from "../../../services/api";
import { useAuth } from "../../../context/AuthContext";
import MobileAppLayout from "../../../layouts/MobileAppLayout";
import api from "../../../services/api";
import { GameButton, GameCard, GameHero, GameIcon, RingProgress, SegmentedControl } from "../../../components/game";
import { formatDate } from "../../../lib/dateFormat";

type AttendanceStatus = "present" | "late" | "absent" | "excused";

interface AttendanceRecord {
  user_id: string;
  status: AttendanceStatus;
  notes?: string;
  reason?: string;
}

interface AthleteWithAttendance extends Athlete {
  attendance?: {
    status: AttendanceStatus;
    notes?: string;
    reason?: string;
  };
}

export default function AppCoachAttendance() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { clubs: ctxClubs, activeClubId: ctxClubId } = useAuth();

  const [sessions, setSessions] = useState<ClubSession[]>([]);
  const [selectedSession, setSelectedSession] = useState<ClubSession | null>(null);
  const [athletes, setAthletes] = useState<AthleteWithAttendance[]>([]);
  const [attendanceRecords, setAttendanceRecords] = useState<Record<string, AttendanceRecord>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [loadingAthletes, setLoadingAthletes] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [filter, setFilter] = useState<"today" | "upcoming" | "all">("today");
  const [editingReason, setEditingReason] = useState<string | null>(null);
  const [reasonText, setReasonText] = useState("");
  const [attachments, setAttachments] = useState<SessionAttachment[]>([]);
  const [loadingAttachments, setLoadingAttachments] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [showAttachments, setShowAttachments] = useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const activeClubId = ctxClubId || ctxClubs[0]?.id;
  const sessionIdParam = searchParams.get("session");

  useEffect(() => {
    const loadSessions = async () => {
      if (!activeClubId) return;
      setLoading(true);
      setError(false);
      try {
        const sessionsData = await getClubSessions(activeClubId);
        setSessions(sessionsData);

        // Auto-select session from URL param
        if (sessionIdParam) {
          const session = sessionsData.find(s => s.id === sessionIdParam);
          if (session) {
            setSelectedSession(session);
          }
        }
      } catch (error) {
        console.error("Failed to load sessions:", error);
        setError(true);
      }
      setLoading(false);
    };
    loadSessions();
  }, [activeClubId, sessionIdParam]);

  // Load athletes when session is selected
  useEffect(() => {
    const loadAthletes = async () => {
      if (!selectedSession) {
        setAthletes([]);
        return;
      }
      setLoadingAthletes(true);
      try {
        // Get team athletes
        const athletesData = await getTeamAthletes(selectedSession.team_id);

        // Get existing attendance for this session
        const { data: existingAttendance } = await api.get(`/sessions/${selectedSession.id}/attendance`);

        // Map attendance to athletes
        const athletesWithAttendance: AthleteWithAttendance[] = athletesData.data.map((athlete: Athlete) => {
          const existing = existingAttendance?.find((a: any) => a.user_id === athlete.id);
          return {
            ...athlete,
            attendance: existing ? {
              status: existing.status,
              notes: existing.notes,
              reason: existing.reason,
            } : undefined,
          };
        });

        setAthletes(athletesWithAttendance);

        // Initialize attendance records from existing data
        const records: Record<string, AttendanceRecord> = {};
        athletesWithAttendance.forEach((athlete: AthleteWithAttendance) => {
          if (athlete.attendance) {
            records[athlete.id] = {
              user_id: athlete.id,
              status: athlete.attendance.status,
              notes: athlete.attendance.notes,
              reason: athlete.attendance.reason,
            };
          }
        });
        setAttendanceRecords(records);
      } catch (error) {
        console.error("Failed to load athletes:", error);
      }
      setLoadingAthletes(false);
    };
    loadAthletes();
  }, [selectedSession]);

  // Load attachments when session is selected
  useEffect(() => {
    const loadAttachments = async () => {
      if (!selectedSession) {
        setAttachments([]);
        return;
      }
      setLoadingAttachments(true);
      try {
        const data = await getSessionAttachments(selectedSession.id);
        setAttachments(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("Failed to load attachments:", error);
        setAttachments([]);
      }
      setLoadingAttachments(false);
    };
    loadAttachments();
  }, [selectedSession]);

  // Filter sessions
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  const filteredSessions = sessions.filter(s => {
    const sessionDate = new Date(s.start_at);
    if (filter === "today") {
      return sessionDate >= today && sessionDate < tomorrow;
    }
    if (filter === "upcoming") {
      return sessionDate >= now;
    }
    return true;
  }).sort((a, b) => {
    return new Date(a.start_at).getTime() - new Date(b.start_at).getTime();
  });

  const handleSelectSession = (session: ClubSession) => {
    setSelectedSession(session);
    setSearchParams({ session: session.id });
  };

  const handleCloseAttendance = () => {
    setSelectedSession(null);
    setSearchParams({});
    setAthletes([]);
    setAttendanceRecords({});
    setSaveSuccess(false);
    setAttachments([]);
    setShowAttachments(false);
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !selectedSession) return;

    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      alert(t("coach.fileTooLarge", "Fișierul este prea mare (max 10MB)"));
      return;
    }

    setUploadingFile(true);
    try {
      const attachment = await uploadSessionAttachment(selectedSession.id, file);
      setAttachments(prev => [...prev, attachment]);
    } catch (error: any) {
      console.error("Failed to upload file:", error);
      // The server explains WHY (unreadable image, too large, wrong type) —
      // showing a fixed generic string threw that away.
      alert(
        error?.response?.data?.error ||
          t("coach.uploadError", "Nu s-a putut încărca fișierul")
      );
    }
    setUploadingFile(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleDeleteAttachment = async (attachmentId: string) => {
    if (!selectedSession) return;
    if (!window.confirm(t("coach.confirmDeleteAttachment", "Sigur vrei să ștergi acest fișier?"))) return;

    try {
      await deleteSessionAttachment(selectedSession.id, attachmentId);
      setAttachments(prev => prev.filter(a => a.id !== attachmentId));
    } catch (error) {
      console.error("Failed to delete attachment:", error);
      alert(t("coach.deleteError", "Nu s-a putut șterge fișierul"));
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const isImageFile = (fileType: string) => {
    return fileType.startsWith("image/");
  };

  // Per-athlete saving spinner so the row pill can show a small
  // pulse while we round-trip to /sessions/:id/attendance for THAT
  // athlete. We don't gate the whole list on a global spinner —
  // multiple rapid clicks on different athletes should each save
  // independently.
  const [savingAthleteIds, setSavingAthleteIds] = useState<Set<string>>(new Set());

  // Internal: send one record to the backend. Used by every UI
  // handler that mutates attendance. Optimistic update happens in the
  // calling sites BEFORE this fires so the UI is instant; on failure
  // the toast shows a generic error (we don't roll back — the
  // hand-clicked state is usually what the coach wants and the page
  // doesn't refresh).
  const saveOneRecord = async (record: AttendanceRecord) => {
    if (!selectedSession) return;
    setSavingAthleteIds((prev) => {
      const next = new Set(prev);
      next.add(record.user_id);
      return next;
    });
    try {
      await setAttendance(selectedSession.id, [record]);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 1500);
    } catch (err) {
      console.error("Failed to save attendance:", err);
    } finally {
      setSavingAthleteIds((prev) => {
        const next = new Set(prev);
        next.delete(record.user_id);
        return next;
      });
    }
  };

  // Bulk save — used by "Marchează toți prezenți" and "Curăță toate"
  // so we don't fire one request per athlete.
  const saveManyRecords = async (records: AttendanceRecord[]) => {
    if (!selectedSession || records.length === 0) return;
    setSaving(true);
    try {
      await setAttendance(selectedSession.id, records);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 1500);
    } catch (err) {
      console.error("Failed to save attendance:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = (athleteId: string, status: AttendanceStatus) => {
    const record: AttendanceRecord = {
      user_id: athleteId,
      status,
      notes: attendanceRecords[athleteId]?.notes,
      reason: attendanceRecords[athleteId]?.reason,
    };
    // Optimistic update — the UI flips instantly, save fires in the
    // background. Page never refreshes / closes.
    setAttendanceRecords((prev) => ({ ...prev, [athleteId]: record }));
    saveOneRecord(record);
  };

  const handleReasonSave = (athleteId: string) => {
    const record: AttendanceRecord = {
      user_id: athleteId,
      status: (attendanceRecords[athleteId]?.status as AttendanceStatus) || "absent",
      notes: attendanceRecords[athleteId]?.notes,
      reason: reasonText,
    };
    setAttendanceRecords((prev) => ({ ...prev, [athleteId]: record }));
    setEditingReason(null);
    setReasonText("");
    saveOneRecord(record);
  };


  const getStatusColor = (status?: AttendanceStatus) => {
    switch (status) {
      case "present": return "bg-emerald-500";
      case "late": return "bg-amber-500";
      case "absent": return "bg-red-500";
      case "excused": return "bg-blue-500";
      default: return "bg-slate-200";
    }
  };

  const getStatusLabel = (status: AttendanceStatus) => {
    switch (status) {
      case "present": return t("coach.present", "Prezent");
      case "late": return t("coach.late", "Întârziat");
      case "absent": return t("coach.absent", "Absent");
      case "excused": return t("coach.excused", "Scuzat");
    }
  };

  // Mark all as present — same optimistic-then-save pattern as a
  // single click, but batched into one POST so the network cost is
  // O(1) instead of O(N).
  const handleMarkAllPresent = () => {
    const records: Record<string, AttendanceRecord> = {};
    const toSave: AttendanceRecord[] = [];
    athletes.forEach((athlete) => {
      const rec: AttendanceRecord = {
        user_id: athlete.id,
        status: "present",
        notes: attendanceRecords[athlete.id]?.notes,
        reason: attendanceRecords[athlete.id]?.reason,
      };
      records[athlete.id] = rec;
      toSave.push(rec);
    });
    setAttendanceRecords(records);
    saveManyRecords(toSave);
  };

  // Clear all marks locally. We do NOT push DELETEs to the server here
  // because there is no bulk-clear endpoint; the next per-row click will
  // overwrite whatever was last stored. If the coach refreshes the page
  // the original server state comes back, which is the expected "undo"
  // behaviour.
  const handleClearAll = () => {
    const records: Record<string, AttendanceRecord> = {};
    athletes.forEach((athlete) => {
      records[athlete.id] = {
        user_id: athlete.id,
        status: undefined as any,
        notes: attendanceRecords[athlete.id]?.notes,
        reason: attendanceRecords[athlete.id]?.reason,
      };
    });
    setAttendanceRecords(records);
  };

  const hasAnyMark = Object.values(attendanceRecords).some(r => !!r?.status);

  // Attendance can only be marked/changed ON the session's own day. Past or
  // future sessions are view-only (the backend enforces the same rule).
  const isSameCalendarDay = (iso: string) => {
    const d = new Date(iso);
    const n = new Date();
    return d.getFullYear() === n.getFullYear() && d.getMonth() === n.getMonth() && d.getDate() === n.getDate();
  };
  const canEdit = !!selectedSession && isSameCalendarDay(selectedSession.start_at);

  return (
    <MobileAppLayout>
      <div className="pb-6">
        {/* Header */}
        <GameHero flow className="rounded-br-3xl px-4 pt-3 pb-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h1 className="text-xl font-extrabold">{t("coach.attendance", "Prezență")}</h1>
              <p className="text-white/70 text-[13px] mt-0.5">{t("coach.attendanceSubtitle", "Marchează prezența la antrenamente")}</p>
            </div>
            {/* The monthly report, from the page where a coach is already
                thinking about attendance. */}
            <Link
              to="/app/coach/reports"
              className="flex-shrink-0 px-3 py-1.5 text-xs font-semibold rounded-lg bg-white/15 text-white hover:bg-white/25"
            >
              {t("coach.reports", "Rapoarte")}
            </Link>
          </div>
        </GameHero>

        <div className="px-4 pt-5 space-y-4">
        {!selectedSession ? (
          <>
            {/* Filter Tabs */}
            <SegmentedControl<"today" | "upcoming" | "all">
              className="flex w-full [&>button]:flex-1"
              options={[
                { value: "today", label: t("coach.today", "Azi") },
                { value: "upcoming", label: t("coach.upcoming", "Viitoare") },
                { value: "all", label: t("coach.all", "Toate") },
              ]}
              value={filter}
              onChange={setFilter}
            />

            {/* Sessions List */}
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-8 h-8 rounded-full border-[3px] border-amber-500 border-t-transparent animate-spin"></div>
              </div>
            ) : error ? (
              <GameCard className="p-6 text-center">
                <h3 className="text-base font-bold text-slate-800 mb-1">{t("common.loadErrorTitle", "Ceva n-a mers")}</h3>
                <p className="text-sm text-slate-500">
                  {t("common.loadError", "Nu am putut încărca datele. Verifică conexiunea și încearcă din nou.")}
                </p>
                <button
                  onClick={() => window.location.reload()}
                  className="mt-3 px-4 py-2 text-sm font-semibold rounded-lg bg-indigo-600 text-white"
                >
                  {t("common.retry", "Reîncearcă")}
                </button>
              </GameCard>
            ) : filteredSessions.length === 0 ? (
              <GameCard className="p-8 text-center">
                <div className="w-16 h-16 mx-auto mb-4 bg-slate-100 rounded-2xl flex items-center justify-center">
                  <svg className="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                </div>
                <p className="text-slate-500">
                  {filter === "today"
                    ? t("coach.noSessionsToday", "Nicio sesiune programată azi")
                    : t("coach.noSessions", "Nicio sesiune găsită")}
                </p>
              </GameCard>
            ) : (
              <div className="space-y-3">
                {filteredSessions.map(session => (
                  <button
                    key={session.id}
                    onClick={() => handleSelectSession(session)}
                    className="w-full p-4 text-left bg-white border border-slate-100 rounded-2xl shadow-[0_3px_0_#eef2f8] active:translate-y-[2px] transition-transform"
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-14 h-14 rounded-2xl flex flex-col items-center justify-center text-white ${
                        new Date(session.start_at) >= today && new Date(session.start_at) < tomorrow
                          ? "bg-gradient-to-br from-amber-400 to-amber-500 shadow-[0_3px_0_#b45309]"
                          : "bg-gradient-to-br from-slate-500 to-slate-600 shadow-[0_3px_0_#334155]"
                      }`}>
                        <span className="text-lg font-extrabold leading-none">
                          {new Date(session.start_at).getDate()}
                        </span>
                        <span className="text-xs uppercase">
                          {new Date(session.start_at).toLocaleString("ro-RO", { month: "short" })}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-bold truncate text-slate-800">{session.title}</h3>
                        <p className="text-sm text-slate-500">{session.team?.name}</p>
                        <p className="text-xs text-slate-400">
                          {new Date(session.start_at).toLocaleTimeString("ro-RO", { hour: "2-digit", minute: "2-digit" })}
                          {" - "}
                          {new Date(session.end_at).toLocaleTimeString("ro-RO", { hour: "2-digit", minute: "2-digit" })}
                        </p>
                      </div>
                      <div className="text-right">
                        {session._count && session._count.attendance > 0 ? (
                          <span className="px-2.5 py-1 bg-emerald-100 text-emerald-700 text-xs font-bold rounded-full">
                            {session._count.attendance} {t("coach.recorded", "înregistrate")}
                          </span>
                        ) : (
                          <span className="px-2.5 py-1 bg-amber-100 text-amber-700 text-xs font-bold rounded-full">
                            {t("coach.notRecorded", "De completat")}
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </>
        ) : (
          /* Attendance Form */
          <div className="space-y-4">
            {/* Back to the session list — a labelled link instead of a bare
                X, so it's clear where it goes. */}
            <button
              onClick={handleCloseAttendance}
              className="-mt-1 inline-flex items-center gap-1.5 text-sm font-bold text-slate-500 hover:text-slate-700 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              {t("coach.backToSessions", "Toate sesiunile")}
            </button>

            {/* Session Info — light card, slate text (was amber with white
                text, which was hard to read). */}
            <GameCard className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-2xl bg-amber-100 flex flex-col items-center justify-center text-amber-700 shrink-0">
                  <span className="text-base font-extrabold leading-none">{new Date(selectedSession.start_at).getDate()}</span>
                  <span className="text-[9px] font-bold uppercase">{new Date(selectedSession.start_at).toLocaleString("ro-RO", { month: "short" })}</span>
                </div>
                <div className="min-w-0">
                  <h2 className="text-base font-extrabold text-slate-800 truncate">{selectedSession.title}</h2>
                  <p className="text-sm text-slate-500 truncate">{selectedSession.team?.name}</p>
                  <p className="mt-0.5 text-xs text-slate-400">
                    {new Date(selectedSession.start_at).toLocaleDateString("ro-RO", { weekday: "long", day: "numeric", month: "long" })}
                    {" • "}
                    {new Date(selectedSession.start_at).toLocaleTimeString("ro-RO", { hour: "2-digit", minute: "2-digit" })}
                  </p>
                </div>
              </div>
            </GameCard>

            {/* Attendance can only be edited on the session's own day. */}
            {!canEdit && (
              <div className="rounded-2xl bg-amber-50 border border-amber-200 px-4 py-3 flex items-start gap-2.5">
                <svg className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                </svg>
                <p className="text-sm font-semibold text-amber-800">
                  {t("coach.attendanceLockedDay", "Prezența se poate marca doar în ziua sesiunii. Aici o poți doar vizualiza.")}
                </p>
              </div>
            )}

            {/* Quick Actions */}
            <div className="flex gap-2">
              {canEdit && (
                <GameButton
                  variant="mint"
                  onClick={handleMarkAllPresent}
                  className="flex-1 !py-2.5 !text-sm !rounded-xl"
                >
                  {t("coach.markAllPresent", "Marchează toți prezenți")}
                </GameButton>
              )}
              {canEdit && hasAnyMark && (
                <GameButton
                  variant="ghost"
                  onClick={handleClearAll}
                  className="!w-auto !py-2.5 !px-3 !text-sm !rounded-xl"
                  title={t("coach.clearAllMarks", "Resetează toate")}
                >
                  {t("coach.clearAllMarks", "Resetează")}
                </GameButton>
              )}
              <button
                onClick={() => setShowAttachments(!showAttachments)}
                className={`px-3 py-2 text-sm font-medium transition-colors rounded-xl flex items-center gap-1.5 ${
                  showAttachments || (attachments?.length ?? 0) > 0
                    ? "bg-blue-100 text-blue-700 hover:bg-blue-200"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                </svg>
                {(attachments?.length ?? 0) > 0 && <span>{(attachments?.length ?? 0)}</span>}
              </button>
            </div>

            {/* Attachments Section */}
            {showAttachments && (
              <GameCard className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-bold text-slate-800">
                    {t("coach.attachments", "Fișiere atașate")}
                  </h3>
                  <label className="px-3 py-1.5 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 cursor-pointer transition-colors flex items-center gap-1.5">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    {uploadingFile ? t("coach.uploading", "Se încarcă...") : t("coach.addFile", "Adaugă")}
                    <input
                      ref={fileInputRef}
                      type="file"
                      className="hidden"
                      onChange={handleFileUpload}
                      disabled={uploadingFile}
                      accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
                    />
                  </label>
                </div>

                {loadingAttachments ? (
                  <div className="flex items-center justify-center py-6">
                    <div className="w-6 h-6 rounded-full border-2 border-blue-500 border-t-transparent animate-spin"></div>
                  </div>
                ) : (attachments?.length ?? 0) === 0 ? (
                  <div className="py-6 text-center text-slate-400 text-sm">
                    <svg className="w-12 h-12 mx-auto mb-2 text-slate-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                    {t("coach.noAttachments", "Niciun fișier atașat")}
                  </div>
                ) : (
                  <div className="space-y-2">
                    {attachments.map(attachment => (
                      <div
                        key={attachment.id}
                        className="flex items-center gap-3 p-2 bg-slate-50 rounded-lg group"
                      >
                        {isImageFile(attachment.file_type) ? (
                          <a
                            href={attachment.file_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0 bg-slate-200"
                          >
                            <img
                              src={attachment.file_url}
                              alt={attachment.file_name}
                              className="w-full h-full object-cover"
                            />
                          </a>
                        ) : (
                          <div className="w-12 h-12 rounded-lg bg-slate-200 flex items-center justify-center flex-shrink-0">
                            <svg className="w-6 h-6 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <a
                            href={attachment.file_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm font-medium text-slate-700 hover:text-blue-600 truncate block"
                          >
                            {attachment.file_name}
                          </a>
                          <p className="text-xs text-slate-400">
                            {formatFileSize(attachment.file_size)} • {formatDate(new Date(attachment.created_at))}
                          </p>
                        </div>
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <a
                            href={attachment.file_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title={t("coach.download", "Descarcă")}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                            </svg>
                          </a>
                          <button
                            onClick={() => handleDeleteAttachment(attachment.id)}
                            className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title={t("coach.delete", "Șterge")}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </GameCard>
            )}

            {/* Athletes Attendance List */}
            {loadingAthletes ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-8 h-8 rounded-full border-[3px] border-amber-500 border-t-transparent animate-spin"></div>
              </div>
            ) : athletes.length === 0 ? (
              <GameCard className="p-8 text-center">
                <div className="w-16 h-16 mx-auto mb-4 bg-slate-100 rounded-2xl flex items-center justify-center">
                  <svg className="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                </div>
                <p className="text-slate-500">{t("coach.noAthletesInTeam", "Niciun sportiv în această echipă")}</p>
              </GameCard>
            ) : (
              <div className="space-y-3">
                {athletes.map(athlete => (
                  <div
                    key={athlete.id}
                    className="p-4 bg-white border border-slate-100 rounded-2xl shadow-[0_3px_0_#eef2f8]"
                  >
                    <div className="flex items-center gap-3">
                      {/* Avatar — uses the athlete's uploaded profile photo
                          when available, falls back to the first letter.
                          Status is rendered as a small colored dot anchored
                          to the bottom-right corner of the avatar instead of
                          a separate slate bar (which was confusing before
                          the coach realized it was the status indicator). */}
                      <div className="relative flex-shrink-0">
                        {athlete.profile_url ? (
                          <img
                            src={athlete.profile_url}
                            alt={athlete.full_name || athlete.email}
                            className="w-10 h-10 rounded-full object-cover"
                          />
                        ) : (
                          <div className="flex items-center justify-center w-10 h-10 font-semibold text-white rounded-full bg-gradient-to-br from-slate-500 to-slate-600">
                            {athlete.full_name?.charAt(0) || "?"}
                          </div>
                        )}
                        {attendanceRecords[athlete.id]?.status && (
                          <span
                            className={`absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full ring-2 ring-white ${getStatusColor(attendanceRecords[athlete.id]?.status)}`}
                            title={getStatusLabel(attendanceRecords[athlete.id]!.status)}
                            aria-label={getStatusLabel(attendanceRecords[athlete.id]!.status)}
                          />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate text-slate-800">{athlete.full_name || t("coach.unnamed", "Fără nume")}</p>
                        <p className="text-xs truncate text-slate-500">{athlete.email}</p>
                      </div>
                    </div>

                    {/* Status Buttons */}
                    <div className="flex gap-2 mt-3">
                      {(["present", "late", "absent", "excused"] as AttendanceStatus[]).map(status => {
                        const active = attendanceRecords[athlete.id]?.status === status;
                        return (
                        <button
                          key={status}
                          disabled={!canEdit}
                          onClick={() => canEdit && handleStatusChange(athlete.id, status)}
                          className={`flex-1 py-2 px-2 rounded-xl text-xs font-bold transition-transform ${canEdit ? "active:translate-y-[1px]" : "cursor-default"} ${
                            active
                              ? status === "present" ? "bg-emerald-500 text-white shadow-[0_2px_0_#059669]" :
                                status === "late" ? "bg-amber-500 text-white shadow-[0_2px_0_#b45309]" :
                                status === "absent" ? "bg-red-500 text-white shadow-[0_2px_0_#be123c]" :
                                "bg-blue-500 text-white shadow-[0_2px_0_#1e40af]"
                              : !canEdit ? "bg-slate-50 text-slate-300" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                          }`}
                        >
                          {getStatusLabel(status)}
                        </button>
                        );
                      })}
                    </div>

                    {/* Reason Field (for absent/excused) */}
                    {(attendanceRecords[athlete.id]?.status === "absent" ||
                      attendanceRecords[athlete.id]?.status === "excused") && (
                      <div className="mt-3">
                        {!canEdit ? (
                          attendanceRecords[athlete.id]?.reason ? (
                            <p className="px-3 py-2 text-sm rounded-lg bg-slate-50 text-slate-500 italic">"{attendanceRecords[athlete.id]?.reason}"</p>
                          ) : null
                        ) : editingReason === athlete.id ? (
                          <div className="flex gap-2">
                            <input
                              type="text"
                              value={reasonText}
                              onChange={e => setReasonText(e.target.value)}
                              placeholder={t("coach.reasonPlaceholder", "Motivul absenței...")}
                              className="flex-1 px-3 py-2 text-sm border rounded-lg bg-slate-50 border-slate-200 focus:outline-none focus:ring-2 focus:ring-amber-500 text-slate-700"
                              autoFocus
                            />
                            <button
                              onClick={() => handleReasonSave(athlete.id)}
                              className="px-3 py-2 text-sm font-medium text-white rounded-lg bg-amber-500"
                            >
                              {t("common.save", "Salvează")}
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => {
                              setEditingReason(athlete.id);
                              setReasonText(attendanceRecords[athlete.id]?.reason || "");
                            }}
                            className="w-full px-3 py-2 text-sm text-left rounded-lg bg-slate-50 text-slate-500 hover:bg-slate-100"
                          >
                            {attendanceRecords[athlete.id]?.reason || t("coach.addReason", "+ Adaugă motiv")}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Progress + auto-save indicator. The standalone "Salvează
                prezența" button was removed: every status pill click,
                reason save, and bulk mark now auto-saves and the page
                stays put. We just surface a brief checkmark when a
                save lands so the coach knows it stuck. */}
            {canEdit && athletes.length > 0 && (() => {
              const markedCount = Object.values(attendanceRecords).filter((r) => !!r?.status).length;
              const markedPct = athletes.length > 0 ? Math.round((markedCount / athletes.length) * 100) : 0;
              return (
              <div className="sticky p-3 border shadow-[0_3px_0_#eef2f8] bg-white/90 backdrop-blur-sm rounded-2xl border-slate-100">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <RingProgress pct={markedPct} size={40} stroke={5} color="#10b981">
                      <span className="text-[10px] font-extrabold text-slate-700 tabular-nums">{markedPct}%</span>
                    </RingProgress>
                    <p className="text-xs font-bold text-slate-500">
                      {markedCount} / {athletes.length} {t("coach.marked", "marcați")}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    {(saving || savingAthleteIds.size > 0) ? (
                      <span className="flex items-center gap-1.5 text-amber-600">
                        <div className="w-3 h-3 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
                        {t("common.saving", "Se salvează...")}
                      </span>
                    ) : saveSuccess ? (
                      <span className="flex items-center gap-1 text-emerald-600">
                        <GameIcon name="check" className="w-4 h-4" />
                        {t("coach.attendanceSaved", "Salvat")}
                      </span>
                    ) : (
                      <span className="text-slate-400">{t("coach.autoSaves", "Auto-salvare activă")}</span>
                    )}
                  </div>
                </div>
              </div>
              );
            })()}
          </div>
        )}
        </div>
      </div>
    </MobileAppLayout>
  );
}
